import { FieldStore, FormStore, ListFieldStore, required } from '@tom-ui/utils'
import {
  AllocationDestinationDto,
  AllocationRuleDto,
  CarrierType,
  CarrierVisitDirection,
  ContainerHeight,
  ContainerType,
  RuleFacets as ApiRuleFacets,
} from '@yard/app/api'
import { YardPositionBlockRange } from '@yard/components/YardPositionSelector/models/yard-position-block-range.model'
import {
  BooleanSelectOption,
  convertBooleanToBooleanSelectOption,
} from '@yard/utils/boolean-select.util'
import { action, computed, makeObservable, observable } from 'mobx'
import { mapYardPositionDestinationRangeToAllocationDestinationDto } from '../helpers/allocation-rule.mapper'
import {
  AllocationRuleDestination,
  AllocationRuleFormData,
  EnforcementMode,
  RuleFacets,
} from '../models/allocation-rule.model'

export type AllocationDestinationSubFormType = {
  blockId: FieldStore<string>
  minBay: FieldStore<number>
  maxBay: FieldStore<number>
  minRow: FieldStore<number>
  maxRow: FieldStore<number>
  position: FieldStore<number>
}

export type RuleFacetsSubFormType = {
  size: FieldStore<number | undefined | null>
  containerOperatorId: FieldStore<number | undefined | null>
  customerId: FieldStore<number | undefined | null>
  consigneeId: FieldStore<number | undefined | null>
  containerHeight: FieldStore<ContainerHeight | undefined | null>
  containerType: FieldStore<ContainerType | undefined | null>
  carrierType: FieldStore<CarrierType | undefined | null>
  carrierVisitId: FieldStore<number | undefined | null>
  direction: FieldStore<CarrierVisitDirection | undefined | null>
  portOfDischarge: FieldStore<string | undefined | null>
  containerNumber: FieldStore<string | undefined | null>
  isEmpty: FieldStore<BooleanSelectOption | undefined>
  isReefer: FieldStore<BooleanSelectOption | undefined>
  isDangerous: FieldStore<BooleanSelectOption | undefined>
  isOOG: FieldStore<BooleanSelectOption | undefined>
  isRestow: FieldStore<BooleanSelectOption | undefined>
  imoClasses: FieldStore<string[]>
  enforcementMode: FieldStore<EnforcementMode | null>
}

export class AllocationRuleFormStore extends FormStore<AllocationRuleFormData> {
  name: FieldStore<string>
  destinations: ListFieldStore<AllocationDestinationSubFormType>
  facets: RuleFacetsSubFormType

  isUpsertDestinationDialogOpen = false
  editingDestinationIndex?: number
  destinationBlockRange?: YardPositionBlockRange
  destinationDialogHasError = false

  constructor() {
    super()
    makeObservable(this, {
      isUpsertDestinationDialogOpen: observable,
      editingDestinationIndex: observable,
      destinationBlockRange: observable,
      destinationDialogHasError: observable,

      addDestination: action,
      deleteDestination: action,
      reorderDestinations: action,
      saveDestination: action,
      toggleUpsertDestinationDialog: action,
      setDestinationBlockRange: action,
      setDestinationDialogHasError: action,
      clearDangerousGoodsFields: action,

      sortedDestinations: computed,
    })

    this.name = new FieldStore('', required())

    this.destinations = new ListFieldStore<AllocationDestinationSubFormType>([])

    this.facets = this.createFacetsSubForm()
  }

  setValues = (data: AllocationRuleDto) => {
    this.name.setValue(data.name)
    this.destinations.setValue(
      data.destinations?.map((dest: AllocationDestinationDto) =>
        this.createDestinationSubForm(dest),
      ) ?? [],
    )
    this.facets = this.createFacetsSubForm(data.facets, data.enforcementMode)
  }

  validate = () => {
    const isNameValid = this.name.validate()
    const areDestinationsValid = this.destinations.value.every(dest =>
      Object.values(dest).every(field => field.validate()),
    )
    const areFacetsValid = Object.values(this.facets).every(field => field.validate())

    const isDangerousYes = this.facets.isDangerous.value === BooleanSelectOption.True
    if (isDangerousYes && this.facets.enforcementMode.value === null) {
      this.facets.enforcementMode.setError('enforcementModeRequired')
    }
    const isEnforcementModeValid = !isDangerousYes || this.facets.enforcementMode.value !== null

    return isNameValid && areDestinationsValid && areFacetsValid && isEnforcementModeValid
  }

  clearDangerousGoodsFields = () => {
    this.facets.imoClasses.setValue([])
    this.facets.enforcementMode.setValue(null)
  }

  toggleUpsertDestinationDialog = (destinationIndex?: number) => {
    this.setDestinationDialogHasError(false)
    this.setDestinationBlockRange()
    this.isUpsertDestinationDialogOpen = !this.isUpsertDestinationDialogOpen
    this.editingDestinationIndex = destinationIndex

    if (destinationIndex !== undefined) {
      const destination = this.destinations.value[destinationIndex]
      this.destinationBlockRange = {
        blockId: destination.blockId.value,
        minBay: destination.minBay.value,
        maxBay: destination.maxBay.value,
        minRow: destination.minRow.value,
        maxRow: destination.maxRow.value,
      }
    }
  }

  setDestinationBlockRange = (range?: YardPositionBlockRange) => {
    this.destinationBlockRange = range
  }

  setDestinationDialogHasError = (hasError: boolean) => {
    this.destinationDialogHasError = hasError
  }

  saveDestination = () => {
    if (!this.destinationBlockRange) {
      this.setDestinationDialogHasError(true)
      return
    }

    const priority =
      this.editingDestinationIndex !== undefined
        ? this.destinations.value[this.editingDestinationIndex].position.value
        : this.destinations.value.length
    const destinationData: AllocationDestinationDto =
      mapYardPositionDestinationRangeToAllocationDestinationDto(
        this.destinationBlockRange,
        priority,
      )

    if (this.editingDestinationIndex !== undefined) {
      this.updateDestination(this.editingDestinationIndex, destinationData)
    } else {
      this.addDestination(destinationData)
    }

    this.toggleUpsertDestinationDialog()
  }

  addDestination = (value?: AllocationDestinationDto) => {
    this.destinations.push(this.createDestinationSubForm(value))
  }

  updateDestination = (index: number, value: AllocationDestinationDto) => {
    this.destinations.value[index] = this.createDestinationSubForm(value)
  }

  reorderDestinations = (sourceIndex: number, destinationIndex: number) => {
    const moved = this.destinations.value.splice(sourceIndex, 1)[0]
    this.destinations.value.splice(destinationIndex, 0, moved)

    this.destinations.value.forEach((dest, idx) => {
      dest.position.setValue(idx)
    })
  }

  deleteDestination = (index: number) => {
    this.destinations.removeByIndex(index)

    this.destinations.value.forEach((dest, idx) => {
      dest.position.setValue(idx)
    })
  }

  get sortedDestinations() {
    return [...this.destinations.value].sort((a, b) => a.position.value - b.position.value)
  }

  get data(): AllocationRuleFormData {
    return {
      ...super.data,
      name: this.name.data,
      destinations: this.destinations.data as AllocationRuleDestination[],
      facets: Object.fromEntries(
        Object.entries(this.facets).map(([k, v]) => [k, v.data]),
      ) as RuleFacets,
    }
  }

  private createDestinationSubForm = (
    value?: AllocationDestinationDto,
  ): AllocationDestinationSubFormType => ({
    blockId: new FieldStore(value?.blockId ?? '', required()),
    minBay: new FieldStore(value?.minBay ?? 0),
    maxBay: new FieldStore(value?.maxBay ?? 0),
    minRow: new FieldStore(value?.minRow ?? 0),
    maxRow: new FieldStore(value?.maxRow ?? 0),
    position: new FieldStore(value?.priority ?? 0),
  })

  private createFacetsSubForm = (
    value?: ApiRuleFacets,
    enforcementMode?: EnforcementMode | null,
  ): RuleFacetsSubFormType => ({
    size: new FieldStore(value?.size),
    isEmpty: new FieldStore(convertBooleanToBooleanSelectOption(value?.isEmpty)),
    isReefer: new FieldStore(convertBooleanToBooleanSelectOption(value?.isReefer)),
    isDangerous: new FieldStore(convertBooleanToBooleanSelectOption(value?.isDangerous)),
    containerOperatorId: new FieldStore(value?.containerOperatorId),
    customerId: new FieldStore(value?.customerId),
    consigneeId: new FieldStore(value?.consigneeId),
    portOfDischarge: new FieldStore(value?.portOfDischarge),
    containerNumber: new FieldStore(value?.containerNumber),
    isOOG: new FieldStore(convertBooleanToBooleanSelectOption(value?.isOOG)),
    isRestow: new FieldStore(convertBooleanToBooleanSelectOption(value?.isRestow)),
    containerHeight: new FieldStore(value?.containerHeight),
    containerType: new FieldStore(value?.containerType),
    carrierType: new FieldStore(value?.carrierType),
    carrierVisitId: new FieldStore(value?.carrierVisitId),
    direction: new FieldStore(value?.direction),
    imoClasses: new FieldStore<string[]>(value?.imoClasses ?? []),
    enforcementMode: new FieldStore<EnforcementMode | null>(enforcementMode ?? null),
  })
}
